"use client";

import * as React from "react";
import { cn } from "../lib/utils";

export interface Notification {
  id: string;
  title: string;
  description?: string;
  time: string;
  read: boolean;
  type?: "info" | "success" | "warning" | "error";
  href?: string;
}

export interface NotificationBellProps {
  notifications: Notification[];
  onRead?: (id: string) => void;
  onReadAll?: () => void;
  onClick?: (notification: Notification) => void;
  className?: string;
}

export function NotificationBell({
  notifications,
  onRead,
  onReadAll,
  onClick,
  className,
}: NotificationBellProps) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);
  const unreadCount = notifications.filter((n) => !n.read).length;

  // Close on outside click
  React.useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const typeColors = {
    info: "bg-blue-500",
    success: "bg-green-500",
    warning: "bg-yellow-500",
    error: "bg-red-500",
  };

  return (
    <div ref={ref} className={cn("relative", className)}>
      {/* Bell Button */}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="relative flex h-10 w-10 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
      >
        <BellIcon />
        {unreadCount > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-error px-1 text-[10px] font-bold text-white">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute right-0 top-full z-50 mt-2 w-80 overflow-hidden rounded-xl border border-border bg-card shadow-xl animate-[scaleIn_0.15s_ease-out] sm:w-96">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <h3 className="text-sm font-semibold text-foreground">
              Notificacoes
              {unreadCount > 0 && (
                <span className="ml-2 rounded-full bg-error/10 px-2 py-0.5 text-xs text-error">
                  {unreadCount} nova{unreadCount !== 1 ? "s" : ""}
                </span>
              )}
            </h3>
            {unreadCount > 0 && onReadAll && (
              <button
                type="button"
                onClick={() => { onReadAll(); }}
                className="text-xs text-primary-600 hover:text-primary-700"
              >
                Marcar todas como lidas
              </button>
            )}
          </div>

          {/* List */}
          <div className="max-h-[360px] overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10">
                <BellOffIcon />
                <p className="mt-2 text-sm text-muted-foreground">Nenhuma notificacao</p>
              </div>
            ) : (
              notifications.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => {
                    if (!n.read && onRead) onRead(n.id);
                    onClick?.(n);
                  }}
                  className={cn(
                    "flex w-full gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/50",
                    !n.read && "bg-primary-50/50 dark:bg-primary-900/10"
                  )}
                >
                  {/* Dot */}
                  <div className="mt-1.5 shrink-0">
                    <div
                      className={cn(
                        "h-2 w-2 rounded-full",
                        n.read ? "bg-transparent" : typeColors[n.type || "info"]
                      )}
                    />
                  </div>
                  {/* Content */}
                  <div className="min-w-0 flex-1">
                    <p className={cn("text-sm", n.read ? "text-muted-foreground" : "font-medium text-foreground")}>
                      {n.title}
                    </p>
                    {n.description && (
                      <p className="mt-0.5 text-xs text-muted-foreground line-clamp-2">{n.description}</p>
                    )}
                    <p className="mt-1 text-[10px] text-muted-foreground/70">{n.time}</p>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function BellIcon() {
  return (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
    </svg>
  );
}

function BellOffIcon() {
  return (
    <svg className="h-10 w-10 text-muted-foreground/30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
    </svg>
  );
}
